import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  alpha,
  useMediaQuery,
  Toolbar,
  AppBar,
  Drawer,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Select,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  HowToVote as VoteIcon,
  BarChart as ResultsIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingIcon,
  QueryStats as ForecastIcon,
  Refresh as RefreshIcon,
  Menu as MenuIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
  CompareArrows as CompareIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  Description as ManifestoIcon,
  AccessTime as CountdownIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Download as DownloadIcon,
  RssFeed as RssFeedIcon,
  AccountBalance as ActorsIcon,
  Hub as NetworkIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Language as LanguageIcon,
  // ... imports
} from '@mui/icons-material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Collapse } from '@mui/material';

import { theme as appTheme } from '../theme';
import { ViewModeProvider, useViewMode } from '../contexts/ViewModeContext';
import { useUserRole, UserRole } from '../contexts/UserRoleContext';
import { useRefresh } from '../contexts/RefreshContext';
import DataService from '../services/dataService';
import { useLayoutActions } from '../contexts/LayoutActionsContext';
import { nigeriaStates } from '../data/nigeriaMapData';

const ROLE_LABELS: Record<UserRole, string> = { voter: 'Voter', journalist: 'Press', analyst: 'Analyst' };
const ROLE_COLORS: Record<UserRole, string> = { voter: appTheme.palette.success.main, journalist: appTheme.palette.secondary.main, analyst: appTheme.palette.info.main };

// Filter option constants
const TIME_RANGE_OPTIONS = [
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const REGION_OPTIONS = [
  { value: 'all', label: 'All Regions' },
  { value: 'north', label: 'North' },
  { value: 'south', label: 'South' },
  { value: 'east', label: 'East' },
  { value: 'west', label: 'West' },
];

const CATEGORY_OPTIONS = ['All', 'Economy', 'Education', 'Healthcare', 'Infrastructure', 'Security'];

const ELECTION_TYPE_OPTIONS = [
  { value: 'presidential', label: 'Presidential' },
  { value: 'gubernatorial', label: 'Gubernatorial' },
  { value: 'senate', label: 'Senate' },
  { value: 'house', label: 'House of Reps' },
  { value: 'state-assembly', label: 'State Assembly' },
];

const VOTER_STATUS_OPTIONS = [
  { value: 'all', label: 'All Personas' },
  { value: 'active', label: 'Active' },
  { value: 'new', label: 'New' },
];

// Single unified filter that shows relevant sections based on current page
const UnifiedFilter: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const statesList = nigeriaStates.map(s => ({ id: s.id, name: s.name }));
  const [timeRange, setTimeRange] = React.useState('7d');
  const [selectedRegion, setSelectedRegion] = React.useState('all');
  const [newsSources, setNewsSources] = React.useState<{ name: string; isFactCheck: boolean }[]>([]);

  const path = location.pathname;
  const isCandidateProfile = path.startsWith('/candidate/');
  const showParty = path === '/candidates';
  const showElection = path === '/candidates';
  const isPoliticalActors = path === '/political-actors';
  const showState = !isCandidateProfile && !isPoliticalActors;
  const showPoliticalActorFilters = isPoliticalActors;
  const showCategory = path === '/manifestos';
  const showTimeRange = path === '/analytics' || isCandidateProfile;
  const showRegion = path === '/analytics' || isCandidateProfile;
  const showStatus = path === '/voters';
  const showSource = path === '/news';
  const showElectionType = path === '/results';
  const showHistoricalType = path === '/elections';
  const showDataInsightsTab = path === '/data-insights';
  const showForecastHistory = path === '/forecast';

  React.useEffect(() => {
    if (path !== '/news') return;
    fetch('/api/rss/sources')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { name: string; isFactCheck: boolean }[]) =>
        setNewsSources(data.map(s => ({ name: s.name, isFactCheck: s.isFactCheck })))
      )
      .catch(() => setNewsSources([
        { name: 'AllAfrica Nigeria', isFactCheck: false },
        { name: 'Arise News', isFactCheck: false },
        { name: 'BBC Africa', isFactCheck: false },
        { name: 'Blueprint', isFactCheck: false },
        { name: 'BusinessDay', isFactCheck: false },
        { name: 'Channels TV', isFactCheck: false },
        { name: 'Daily Trust', isFactCheck: false },
        { name: 'Dubawa', isFactCheck: true },
        { name: 'FactCheckHub', isFactCheck: true },
        { name: 'HumAngle', isFactCheck: false },
        { name: 'ICIR Nigeria', isFactCheck: false },
        { name: 'Leadership Newspaper', isFactCheck: false },
        { name: 'Nigerian Tribune', isFactCheck: false },
        { name: 'PM News', isFactCheck: false },
        { name: 'Peoples Gazette', isFactCheck: false },
        { name: 'Premium Times', isFactCheck: false },
        { name: 'Punch', isFactCheck: false },
        { name: 'Sahara Reporters', isFactCheck: false },
        { name: 'The Whistler', isFactCheck: false },
        { name: 'Thisday Live', isFactCheck: false },
        { name: 'YNaija', isFactCheck: false },
      ]));
  }, [path]);

  const selectedState = searchParams.get('state');
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedStatus = searchParams.get('status') || 'all';
  const selectedNewsSource = searchParams.get('newsSource') || '';

  const selectedParty = searchParams.get('party') || '';
  const selectedElection = searchParams.get('election') || 'all';
  const selectedElectionType = searchParams.get('electionType') || 'presidential';
  const selectedResultsYear = searchParams.get('year') || '2023';
  const showResultsYear = path === '/results';
  const newsElectionOnly = searchParams.get('electionOnly') !== '0'; // default true
  const showElectionOnly = path === '/news';

  const selectedPaRole = searchParams.get('role') || 'all';
  const selectedPaParty = searchParams.get('affiliation') || 'all';
  const selectedHistType = searchParams.get('histType') || 'All';
  const selectedDiTab = searchParams.get('diTab') || 'President';
  const selectedForecastYear = Number(searchParams.get('forecastYear') || '2023');
  const selectedForecastType = searchParams.get('forecastType') || 'presidential';

  const activeCount = [
    selectedParty ? 1 : 0,
    selectedElection !== 'all' ? 1 : 0,
    selectedState ? 1 : 0,
    isPoliticalActors && selectedPaRole !== 'all' ? 1 : 0,
    isPoliticalActors && selectedPaParty !== 'all' ? 1 : 0,
    selectedCategory !== 'all' ? 1 : 0,
    selectedStatus !== 'all' ? 1 : 0,
    timeRange !== '7d' ? 1 : 0,
    selectedRegion !== 'all' ? 1 : 0,
    selectedNewsSource ? 1 : 0,
    showElectionOnly && !newsElectionOnly ? 1 : 0,
    showElectionType && selectedElectionType !== 'presidential' ? 1 : 0,
    showResultsYear && selectedResultsYear !== '2023' ? 1 : 0,
    showHistoricalType && selectedHistType !== 'All' ? 1 : 0,
    showDataInsightsTab && selectedDiTab !== 'President' ? 1 : 0,
    showForecastHistory && selectedForecastYear !== 2023 ? 1 : 0,
    showForecastHistory && selectedForecastType !== 'presidential' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearAll = () => {
    setSearchParams(p => { p.delete('party'); p.delete('election'); p.delete('state'); p.delete('category'); p.delete('status'); p.delete('newsSource'); p.delete('electionOnly'); p.delete('electionType'); p.delete('role'); p.delete('affiliation'); p.delete('histType'); p.delete('diTab'); p.delete('forecastYear'); p.delete('forecastType'); return p; });
    setTimeRange('7d');
    setSelectedRegion('all');
  };

  return (
    <>
      <Badge badgeContent={activeCount} color="primary" invisible={activeCount === 0}>
        <Button
          variant={activeCount > 0 ? 'contained' : 'text'}
          startIcon={<FilterListIcon />}
          onClick={e => setAnchorEl(e.currentTarget)}
          size="medium"
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {t('filters.filters')}
        </Button>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { p: 2, minWidth: 300, maxWidth: 360, borderRadius: 3, boxShadow: '0 10px 40px -10px rgba(0,0,0,0.15)' } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>{t('filters.filters')}</Typography>
          {activeCount > 0 && (
            <Button size="small" onClick={clearAll} sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem', minWidth: 'auto' }}>
              {t('filters.clearAll')}
            </Button>
          )}
        </Box>

        {showParty && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              Party
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['All', 'APC', 'PDP', 'LP', 'NNPP'].map(party => {
                const active = party === 'All' ? !selectedParty : selectedParty === party;
                return (
                  <Chip
                    key={party} label={party} size="small"
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    onClick={() => setSearchParams(p => { party === 'All' ? p.delete('party') : p.set('party', party); return p; })}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {showElection && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              Election Year
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['all', '2027', '2023'].map(year => (
                <Chip
                  key={year}
                  label={year === 'all' ? 'All Years' : year}
                  size="small"
                  color={selectedElection === year ? 'primary' : 'default'}
                  variant={selectedElection === year ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { year === 'all' ? p.delete('election') : p.set('election', year); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showResultsYear && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              Election Year
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['1999', '2003', '2007', '2011', '2015', '2019', '2023'].map(yr => (
                <Chip
                  key={yr} label={yr} size="small"
                  color={selectedResultsYear === yr ? 'primary' : 'default'}
                  variant={selectedResultsYear === yr ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { p.set('year', yr); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showElectionType && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              Election Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {ELECTION_TYPE_OPTIONS.map(opt => (
                <Chip
                  key={opt.value} label={opt.label} size="small"
                  color={selectedElectionType === opt.value ? 'primary' : 'default'}
                  variant={selectedElectionType === opt.value ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { p.set('electionType', opt.value); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showState && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              {t('filters.state')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={t('filters.allStates')} size="small"
                color={!selectedState ? 'primary' : 'default'}
                variant={!selectedState ? 'filled' : 'outlined'}
                onClick={() => setSearchParams(p => { p.delete('state'); return p; })}
                sx={{ cursor: 'pointer' }}
              />
              {statesList.map(s => (
                <Chip
                  key={s.id} label={s.name} size="small"
                  color={selectedState === s.id ? 'primary' : 'default'}
                  variant={selectedState === s.id ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { p.set('state', s.id); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showCategory && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              {t('filters.category')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {CATEGORY_OPTIONS.map(c => {
                const val = c === 'All' ? 'all' : c;
                const active = selectedCategory === val;
                return (
                  <Chip
                    key={c} label={c} size="small"
                    color={active ? 'primary' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    onClick={() => setSearchParams(p => { val === 'all' ? p.delete('category') : p.set('category', val); return p; })}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {showTimeRange && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              {t('filters.timeRange')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {TIME_RANGE_OPTIONS.map(opt => (
                <Chip
                  key={opt.value} label={opt.label} size="small"
                  color={timeRange === opt.value ? 'primary' : 'default'}
                  variant={timeRange === opt.value ? 'filled' : 'outlined'}
                  onClick={() => setTimeRange(opt.value)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showRegion && (
          <Box sx={{ mb: showStatus ? 2 : 0 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              {t('filters.region')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {REGION_OPTIONS.map(opt => (
                <Chip
                  key={opt.value} label={opt.label} size="small"
                  color={selectedRegion === opt.value ? 'primary' : 'default'}
                  variant={selectedRegion === opt.value ? 'filled' : 'outlined'}
                  onClick={() => setSelectedRegion(opt.value)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showStatus && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              {t('filters.personaStatus')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {VOTER_STATUS_OPTIONS.map(opt => (
                <Chip
                  key={opt.value} label={opt.label} size="small"
                  color={selectedStatus === opt.value ? 'primary' : 'default'}
                  variant={selectedStatus === opt.value ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { opt.value === 'all' ? p.delete('status') : p.set('status', opt.value); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showElectionOnly && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              Content
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label="Election only"
                size="small"
                color={newsElectionOnly ? 'primary' : 'default'}
                variant={newsElectionOnly ? 'filled' : 'outlined'}
                onClick={() => setSearchParams(p => { newsElectionOnly ? p.set('electionOnly', '0') : p.delete('electionOnly'); return p; })}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="All topics"
                size="small"
                color={!newsElectionOnly ? 'primary' : 'default'}
                variant={!newsElectionOnly ? 'filled' : 'outlined'}
                onClick={() => setSearchParams(p => { p.set('electionOnly', '0'); return p; })}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        )}

        {showSource && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              {t('filters.newsSource')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={t('filters.allSources')} size="small"
                color={!selectedNewsSource ? 'primary' : 'default'}
                variant={!selectedNewsSource ? 'filled' : 'outlined'}
                onClick={() => setSearchParams(p => { p.delete('newsSource'); return p; })}
                sx={{ cursor: 'pointer' }}
              />
              {newsSources.map(s => (
                <Chip
                  key={s.name}
                  label={s.isFactCheck ? `${s.name} \u{1F50D}` : s.name}
                  size="small"
                  color={selectedNewsSource === s.name ? 'primary' : 'default'}
                  variant={selectedNewsSource === s.name ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { selectedNewsSource === s.name ? p.delete('newsSource') : p.set('newsSource', s.name); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showDataInsightsTab && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              Election Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(['President', 'Governor', 'Senate', 'House', 'State Assembly'] as const).map(label => (
                <Chip
                  key={label} label={label} size="small"
                  color={selectedDiTab === label ? 'primary' : 'default'}
                  variant={selectedDiTab === label ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { label === 'President' ? p.delete('diTab') : p.set('diTab', label); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showHistoricalType && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              Election Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(['All', 'Presidential', 'Senate', 'House', 'Gubernatorial'] as const).map(t => (
                <Chip
                  key={t} label={t} size="small"
                  color={selectedHistType === t ? 'primary' : 'default'}
                  variant={selectedHistType === t ? 'filled' : 'outlined'}
                  onClick={() => setSearchParams(p => { t === 'All' ? p.delete('histType') : p.set('histType', t); return p; })}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {showForecastHistory && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
                Election Year
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[1999, 2003, 2007, 2011, 2015, 2019, 2023].map(yr => (
                  <Chip
                    key={yr} label={yr} size="small"
                    color={selectedForecastYear === yr ? 'primary' : 'default'}
                    variant={selectedForecastYear === yr ? 'filled' : 'outlined'}
                    onClick={() => setSearchParams(p => { yr === 2023 ? p.delete('forecastYear') : p.set('forecastYear', String(yr)); return p; })}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
                Election Type
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  { value: 'presidential', label: 'Presidential' },
                  { value: 'gubernatorial', label: 'Gubernatorial' },
                  { value: 'senatorial', label: 'Senatorial' },
                ].map(opt => (
                  <Chip
                    key={opt.value} label={opt.label} size="small"
                    color={selectedForecastType === opt.value ? 'primary' : 'default'}
                    variant={selectedForecastType === opt.value ? 'filled' : 'outlined'}
                    onClick={() => setSearchParams(p => { opt.value === 'presidential' ? p.delete('forecastType') : p.set('forecastType', opt.value); return p; })}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}

        {showPoliticalActorFilters && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
                Role
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  { value: 'all', label: 'All Roles' },
                  { value: 'minister', label: 'Minister' },
                  { value: 'chairman', label: 'Chairman' },
                  { value: 'commissioner', label: 'Commissioner' },
                  { value: 'spokesperson', label: 'Spokesperson' },
                  { value: 'adviser', label: 'Adviser' },
                  { value: 'secretary', label: 'Secretary' },
                  { value: 'director_general', label: 'Director-General' },
                  { value: 'committee_head', label: 'Committee Head' },
                  { value: 'chief_of_staff', label: 'Chief of Staff' },
                  { value: 'figure', label: 'Figure' },
                  { value: 'other', label: 'Other' },
                ].map(opt => (
                  <Chip
                    key={opt.value} label={opt.label} size="small"
                    color={selectedPaRole === opt.value ? 'primary' : 'default'}
                    variant={selectedPaRole === opt.value ? 'filled' : 'outlined'}
                    onClick={() => setSearchParams(p => { opt.value === 'all' ? p.delete('role') : p.set('role', opt.value); return p; })}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
                Party
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  { value: 'all', label: 'All Parties' },
                  { value: 'APC', label: 'APC' },
                  { value: 'PDP', label: 'PDP' },
                  { value: 'LP', label: 'LP' },
                  { value: 'NNPP', label: 'NNPP' },
                  { value: 'APGA', label: 'APGA' },
                  { value: 'ADC', label: 'ADC' },
                  { value: 'SDP', label: 'SDP' },
                ].map(opt => (
                  <Chip
                    key={opt.value} label={opt.label} size="small"
                    color={selectedPaParty === opt.value ? 'primary' : 'default'}
                    variant={selectedPaParty === opt.value ? 'filled' : 'outlined'}
                    onClick={() => setSearchParams(p => { opt.value === 'all' ? p.delete('affiliation') : p.set('affiliation', opt.value); return p; })}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

interface MenuItemType {
  text: string;
  icon: React.ReactNode;
  path?: string;
  badge?: string;
  children?: MenuItemType[];
}

const LayoutContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [trendingAnchorEl, setTrendingAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const actionsButtonRef = React.useRef<HTMLButtonElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { viewMode, setViewMode } = useViewMode();
  const { refresh, refreshKey } = useRefresh();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { actions: pageActions } = useLayoutActions();

  const handleRefresh = () => {
    setIsRefreshing(true);
    refresh();
    setTimeout(() => setIsRefreshing(false), 800);
  };
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    'Election Info': true,
    'Live Center': true,
    'Analysis': true
  });
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { role, setRole, hasFeature } = useUserRole();

  // Header countdown — next election date derived from data
  const [nextElectionCountdown, setNextElectionCountdown] = useState<{ days: number; hours: number; label: string } | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    const elections = DataService.getElections();
    const upcoming = elections
      .filter((e: any) => new Date(e.date) > new Date())
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
    if (upcoming.length) {
      const next = upcoming[0];
      const update = () => {
        const diff = new Date(next.date).getTime() - Date.now();
        if (diff <= 0) { setNextElectionCountdown(null); return; }
        setNextElectionCountdown({
          days: Math.floor(diff / 86400000),
          hours: Math.floor((diff % 86400000) / 3600000),
          label: next.title,
        });
      };
      update();
      timer = setInterval(update, 60000); // update every minute
    }
    return () => clearInterval(timer);
  }, []);

  const notifications: { id: number; title: string; description: string; time: string; type: string; read: boolean }[] = [];

  // Real-time trending: fetched from /api/candidates/trending. The endpoint
  // ranks by mentions in rss_articles over the last 24h and returns a trend %
  // comparing 24h to the prior 7-day daily average. Replaces the prior static
  // c.votes / c.trend fields, which were 2023 historical and never refreshed.
  const [trendingCandidates, setTrendingCandidates] = useState<Array<{
    id: number;
    name: string;
    party: string;
    trend: 'up' | 'down';
    mentions: string;
    change: string;
    avatar: string;
    profileImage?: string;
    color: string;
  }>>([]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/candidates/trending?limit=3&election=presidential')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((payload) => {
        if (cancelled) return;
        const palette = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.info.main];
        setTrendingCandidates(
          (payload.candidates ?? []).map((c: any, i: number) => ({
            id: c.id,
            name: c.name,
            party: c.party,
            trend: c.trendPct >= 0 ? 'up' : 'down',
            mentions: `${c.mentions24h} mentions / 24h`,
            change: `${c.trendPct >= 0 ? '+' : ''}${c.trendPct}%`,
            avatar: (c.name ?? '').split(' ').map((n: string) => n[0]).join(''),
            profileImage: c.profileImage,
            color: palette[i % palette.length],
          })),
        );
      })
      .catch(() => {
        // Endpoint missing or down → empty state, never fall back to stale c.votes
        if (!cancelled) setTrendingCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, theme]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleTrendingClick = (event: React.MouseEvent<HTMLElement>) => {
    setTrendingAnchorEl(event.currentTarget);
  };

  const handleTrendingClose = () => {
    setTrendingAnchorEl(null);
  };

  const handleActionsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleSearchInput = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (value.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/search?q=${encodeURIComponent(value.trim())}&limit=10`);
        if (r.ok) {
          const data = await r.json();
          setSearchResults(data);
          setSearchOpen(data.length > 0);
        }
      } catch { /* silent */ }
    }, 150);
  }, []);

  const handleSearchSelect = (result: any) => {
    setSearchQuery('');
    setSearchOpen(false);
    setSearchResults([]);
    navigate(result.path ?? '/candidates');
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      setSearchOpen(false);
      navigate(`/candidates?search=${encodeURIComponent(searchQuery.trim())}`);
    }
    if (event.key === 'Escape') {
      setSearchOpen(false);
    }
  };

  const RESULT_TYPE_LABELS: Record<string, string> = {
    candidate: 'Candidate',
    actor: 'Political Actor',
    election: 'Election',
  };
  const RESULT_TYPE_COLORS: Record<string, string> = {
    candidate: '#084608',
    actor: '#e67e22',
    election: '#1976d2',
  };

  const handleGroupClick = (text: string) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setTimeout(() => {
        setOpenGroups(prev => ({ ...prev, [text]: true }));
      }, 150);
    } else {
      setOpenGroups(prev => ({ ...prev, [text]: !prev[text] }));
    }
  };

  const menuItems: MenuItemType[] = [
    { text: t('nav.overview'), icon: <DashboardIcon />, path: '/' },
    {
      text: t('nav.electionInfo'),
      icon: <VoteIcon />,
      children: [
        { text: t('nav.candidates'), icon: <PeopleIcon />, path: '/candidates' },
        { text: t('nav.compare'), icon: <CompareIcon />, path: '/compare' },
        { text: t('nav.manifestos'), icon: <ManifestoIcon />, path: '/manifestos' },
        ...(hasFeature('vote') ? [{ text: t('nav.voters'), icon: <VoteIcon />, path: '/voters', badge: t('nav.voteNow') }] : []),
      ]
    },
    {
      text: t('nav.liveCenter'),
      icon: <TimelineIcon />,
      children: [
        { text: t('nav.elections'), icon: <CountdownIcon />, path: '/elections' },
        { text: t('nav.liveResults'), icon: <ResultsIcon />, path: '/results' },
      ]
    },
    ...(hasFeature('data_insights') ? [{ text: t('nav.dataInsights'), icon: <InsightsIcon />, path: '/data-insights' }] : []),
    { text: 'Forecast', icon: <ForecastIcon />, path: '/forecast' },
    { text: 'Networks', icon: <NetworkIcon />, path: '/networks' },
    { text: 'Political Actors', icon: <ActorsIcon />, path: '/political-actors' },
    { text: t('nav.newsSocial'), icon: <RssFeedIcon />, path: '/news' },
  ];

  const drawerWidth = sidebarCollapsed ? 70 : 250;

  const renderMenuItem = (item: MenuItemType, index: number, nested: boolean = false) => {
    const isSelected = item.path ? location.pathname === item.path : false;
    const hasChildren = item.children && item.children.length > 0;
    const isGroupOpen = openGroups[item.text];

    return (
      <React.Fragment key={item.text + index}>
        <ListItemButton
          onClick={() => {
            if (hasChildren) {
              handleGroupClick(item.text);
            } else if (item.path) {
              handleNavigation(item.path);
            }
          }}
          sx={{
            borderRadius: 1.5,
            mb: 0.25,
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            px: sidebarCollapsed ? 1 : (nested ? 3 : 1.5),
            py: nested ? 0.6 : 0.75,
            backgroundColor: isSelected ? alpha(theme.palette.success.main, 0.15) : 'transparent',
            color: isSelected ? theme.palette.success.main : 'rgba(255,255,255,0.57)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.9)',
            },
          }}
        >
          <ListItemIcon sx={{
            minWidth: sidebarCollapsed ? 'auto' : 30,
            color: isSelected ? theme.palette.success.main : 'white',
            mr: sidebarCollapsed ? 0 : 1.25,
            '& .MuiSvgIcon-root': { fontSize: nested ? '1.1rem' : '1.2rem' },
          }}>
            {item.icon}
          </ListItemIcon>
          {!sidebarCollapsed && (
            <>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: isSelected ? 600 : hasChildren ? 500 : 400,
                  color: isSelected ? theme.palette.success.main : hasChildren ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.57)',
                  letterSpacing: hasChildren && !nested ? '0.01em' : 0,
                }}
              />
              {item.badge && (
                <Chip label={item.badge} size="small" color="secondary" sx={{ height: 20, ml: 1, '& .MuiChip-label': { fontSize: '0.75rem', px: 1 } }} />
              )}
              {hasChildren && (isGroupOpen ? <ExpandLess fontSize="small" sx={{ color: 'info.main', fontSize: '1rem' }} /> : <ExpandMore fontSize="small" sx={{ color: 'info.main', fontSize: '1rem' }} />)}
            </>
          )}
        </ListItemButton>

        {hasChildren && !sidebarCollapsed && (
          <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child, idx) => renderMenuItem(child, idx, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'primary.main' }}>
      <Toolbar sx={{
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: sidebarCollapsed ? 'center' : 'space-between',
        px: sidebarCollapsed ? 1 : 2,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        minHeight: 64,
      }}>
        {!sidebarCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1.5,
              bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.75rem', lineHeight: 1 }}>E</Typography>
            </Box>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>
              Electure
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton onClick={() => setSidebarCollapsed(!sidebarCollapsed)} sx={{ color: 'info.main', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }} size="small">
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: 'info.main' }}>
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 } }}>
        <List sx={{ px: 1, py: 1.5 }}>
          {menuItems.map((item, index) => renderMenuItem(item, index))}
        </List>
      </Box>
      {/* Role switcher — bottom of sidebar */}
      {!sidebarCollapsed && (
        <Box sx={{ px: 1.5, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', mb: 0.75 }}>
            {t('sidebar.viewAs')}
          </Typography>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            size="small"
            fullWidth
            sx={{
              fontSize: '0.875rem',
              color: '#fff',
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ROLE_COLORS[role] },
              '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' },
              bgcolor: 'rgba(255,255,255,0.06)',
            }}
          >
            {(['voter', 'journalist', 'analyst'] as UserRole[]).map((r) => (
              <MenuItem key={r} value={r} sx={{ fontSize: '0.875rem' }}>
                {ROLE_LABELS[r]}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
    </Box>
  );

  const getBreadcrumbText = (path: string): string => {
    // Handle election detail pages specially
    if (path.startsWith('/election/')) {
      const electionId = parseInt(path.split('/')[2]);
      const elections = DataService.getElections();
      const election = (elections as any[]).find((e: any) => String(e.id) === String(electionId));
      if (election) {
        const year = election.date ? new Date(election.date).getFullYear() : '';
        return `Elections / ${election.type} / ${year}`;
      }
      return 'Elections';
    }
    // Handle candidate profile pages specially
    if (path.startsWith('/candidate/')) {
      const candidateId = parseInt(path.split('/')[2]);
      const candidate = DataService.getCandidateById(candidateId);
      if (candidate) {
        // Extract first and last name only
        const nameParts = candidate.name.split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts[nameParts.length - 1];
        return `Candidates / ${firstName} ${lastName}`;
      }
      return 'Candidates';
    }

    const findActiveItem = (items: MenuItemType[], currentPath: string): string | undefined => {
      for (const item of items) {
        if (item.path === currentPath) return item.text;
        if (item.children) {
          const found = findActiveItem(item.children, currentPath);
          if (found) return found;
        }
      }
      return undefined;
    };
    const staticLabels: Record<string, string> = {
      '/': t('breadcrumb.overview'), '/candidates': t('breadcrumb.candidates'), '/compare': t('breadcrumb.compare'),
      '/manifestos': t('breadcrumb.manifestos'), '/voters': t('breadcrumb.voters'), '/elections': t('breadcrumb.elections'),
      '/election-countdown': t('breadcrumb.elections'), '/results': t('breadcrumb.liveResults'),
      '/insights': t('breadcrumb.dataInsights'),
      '/data-insights': t('breadcrumb.dataInsights'),
      '/forecast': 'Forecast',
      '/networks': 'Networks',
      '/news': t('breadcrumb.newsSocial'), '/vote': t('breadcrumb.vote'),
    };
    if (staticLabels[path]) return staticLabels[path];
    return findActiveItem(menuItems, path) || 'Overview';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top Navigation Bar */}
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 0.5, pr: 1, minHeight: 64 }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={handleMobileMenuOpen}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Desktop Sidebar Expand/Collapse Button */}
          {!isMobile && (
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              edge="start"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              sx={{ mr: 0.5 }}
              title={sidebarCollapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo + Page Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                color: theme.palette.primary.main,
                fontFamily: '"Libre Baskerville", Georgia, serif',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Electure
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {location.pathname.startsWith('/candidate/') ? (
                <>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    noWrap
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      display: { xs: 'none', md: 'block' },
                      fontSize: { md: '0.875rem', lg: '1rem' },
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      },
                    }}
                    onClick={() => navigate('/candidates')}
                  >
                    Candidates
                  </Typography>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    noWrap
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      display: { xs: 'none', md: 'block' },
                      fontSize: { md: '0.875rem', lg: '1rem' },
                      mx: 0.5,
                    }}
                  >
                    /
                  </Typography>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    noWrap
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      display: { xs: 'none', md: 'block' },
                      fontSize: { md: '0.875rem', lg: '1rem' },
                    }}
                  >
                    {(() => {
                      const candidateId = parseInt(location.pathname.split('/')[2]);
                      const candidate = DataService.getCandidateById(candidateId);
                      if (candidate) {
                        const nameParts = candidate.name.split(' ');
                        const firstName = nameParts[0];
                        const lastName = nameParts[nameParts.length - 1];
                        return `${firstName} ${lastName}`;
                      }
                      return 'Unknown';
                    })()}
                  </Typography>
                </>
              ) : location.pathname.startsWith('/manifesto/') ? (
                <>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    noWrap
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      display: { xs: 'none', md: 'block' },
                      fontSize: { md: '0.875rem', lg: '1rem' },
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      },
                    }}
                    onClick={() => navigate('/manifestos')}
                  >
                    Manifestos
                  </Typography>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    noWrap
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      display: { xs: 'none', md: 'block' },
                      fontSize: { md: '0.875rem', lg: '1rem' },
                      mx: 0.5,
                    }}
                  >
                    /
                  </Typography>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    noWrap
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      display: { xs: 'none', md: 'block' },
                      fontSize: { md: '0.875rem', lg: '1rem' },
                      maxWidth: { md: 200, lg: 320 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {(() => {
                      const manifestoId = location.pathname.split('/')[2];
                      const m = DataService.getManifestoById(manifestoId);
                      return m?.title || 'Manifesto';
                    })()}
                  </Typography>
                </>
              ) : location.pathname.startsWith('/election/') ? (
                <>
                  <Typography
                    component="span"
                    variant="subtitle1"
                    noWrap
                    sx={{ color: 'text.secondary', fontWeight: 500, display: { xs: 'none', md: 'block' }, fontSize: { md: '0.875rem', lg: '1rem' }, cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                    onClick={() => navigate('/elections')}
                  >
                    Elections
                  </Typography>
                  <Typography component="span" variant="subtitle1" noWrap sx={{ color: 'text.secondary', fontWeight: 500, display: { xs: 'none', md: 'block' }, fontSize: { md: '0.875rem', lg: '1rem' }, mx: 0.5 }}>
                    /
                  </Typography>
                  {(() => {
                    const electionId = parseInt(location.pathname.split('/')[2]);
                    const election = DataService.getElectionById(electionId);
                    const year = election?.date ? new Date(election.date).getFullYear() : '';
                    const type = election?.type || 'Election';
                    return (
                      <>
                        <Typography component="span" variant="subtitle1" noWrap sx={{ color: 'text.secondary', fontWeight: 500, display: { xs: 'none', md: 'block' }, fontSize: { md: '0.875rem', lg: '1rem' } }}>
                          {type}
                        </Typography>
                        {year && (
                          <>
                            <Typography component="span" variant="subtitle1" noWrap sx={{ color: 'text.secondary', fontWeight: 500, display: { xs: 'none', md: 'block' }, fontSize: { md: '0.875rem', lg: '1rem' }, mx: 0.5 }}>/</Typography>
                            <Typography component="span" variant="subtitle1" noWrap sx={{ color: 'text.primary', fontWeight: 600, display: { xs: 'none', md: 'block' }, fontSize: { md: '0.875rem', lg: '1rem' } }}>
                              {year}
                            </Typography>
                          </>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: { xs: 'none', md: 'block' },
                    fontSize: { md: '0.875rem', lg: '1rem' },
                    '&::before': {
                      content: '"/"',
                      mr: 0.5,
                      color: 'divider',
                    },
                  }}
                >
                  {getBreadcrumbText(location.pathname)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Search Bar + Filters */}
          <Box
            ref={searchRef}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              flexGrow: 1,
              maxWidth: { sm: 300, md: 420, lg: 500 },
              mx: { xs: 0.5, sm: 1, md: 2 },
              gap: 0.75,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: searchFocused ? 'primary.main' : 'divider', borderRadius: 2, bgcolor: 'background.default', px: 1.5, transition: 'all 0.2s ease', ...(searchFocused ? { boxShadow: `0 0 0 3px ${alpha(appTheme.palette.primary.main, 0.1)}` } : {}) }}>
              <Box
                component="input"
                placeholder={t('header.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => { setSearchFocused(true); if (searchResults.length > 0) setSearchOpen(true); }}
                onBlur={() => { setSearchFocused(false); setTimeout(() => setSearchOpen(false), 150); }}
                sx={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  bgcolor: 'transparent',
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&::placeholder': { opacity: 0.6 },
                }}
              />
              <UnifiedFilter />
            </Box>

            {/* Search results dropdown */}
            {searchOpen && searchResults.length > 0 && (
              <Box sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 0.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: 4,
                zIndex: 1400,
                overflow: 'hidden',
              }}>
                {searchResults.map((result, i) => (
                  <Box
                    key={`${result.type}-${result.id}-${i}`}
                    onMouseDown={() => handleSearchSelect(result)}
                    sx={{
                      px: 2, py: 1.25,
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      cursor: 'pointer',
                      borderBottom: i < searchResults.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      bgcolor: RESULT_TYPE_COLORS[result.type] ?? '#888',
                    }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{result.label}</Typography>
                      {result.sub && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {result.sub}{result.extra ? ` · ${result.extra}` : ''}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={RESULT_TYPE_LABELS[result.type] ?? result.type}
                      size="small"
                      sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, flexShrink: 0,
                        bgcolor: alpha(RESULT_TYPE_COLORS[result.type] ?? '#888', 0.1),
                        color: RESULT_TYPE_COLORS[result.type] ?? '#888',
                      }}
                    />
                  </Box>
                ))}
                <Box
                  onMouseDown={() => { navigate(`/candidates?search=${encodeURIComponent(searchQuery.trim())}`); setSearchOpen(false); }}
                  sx={{ px: 2, py: 1, bgcolor: 'action.hover', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                >
                  <Typography variant="caption" color="text.secondary">
                    See all results for "{searchQuery}"
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Page-specific actions injected via useLayoutActions() */}
          {pageActions}

          {/* Page Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>

            {/* Detail Page Actions — Share + Bookmark */}
            {(location.pathname.startsWith('/manifesto/') || location.pathname.startsWith('/election/')) && (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                <IconButton
                  size="small"
                  title="Share"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: document.title, url: window.location.href });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(0,0,0,0.04)' } }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  title="Bookmark"
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(0,0,0,0.04)' } }}
                >
                  <BookmarkIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Data Insights Actions */}
            {location.pathname === '/data-insights' && (
              <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => console.log('Download report')}
                  sx={{
                    borderRadius: 20,
                    borderColor: '1px solid',
                    color: 'text.secondary',
                    px: 1,
                    py: 0.5,
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(0,0,0,0.04)' }
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => console.log('Share insights')}
                  sx={{
                    borderRadius: 20,
                    bgcolor: 'primary.main',
                    px: 1,
                    py: 0.5,
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Right side icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 }, pr: { xs: 0.5, sm: 1 } }}>
            {/* View Mode Toggle - Only show on larger screens */}
            {['/manifestos', '/candidates'].includes(location.pathname) && !isMobile && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, next) => next && setViewMode(next)}
                size="small"
                sx={{ bgcolor: 'white', mr: { xs: 0, sm: 0.5 } }}
              >
                <ToggleButton value="card" aria-label="card view" sx={{ px: 1, py: 0.5 }}>
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view" sx={{ px: 1, py: 0.5 }}>
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            
            {/* Election Countdown Chip */}
            {nextElectionCountdown && hasFeature('countdown') && (
              <Box
                onClick={() => navigate('/elections')}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.dark' },
                  transition: 'background 0.15s',
                }}
              >
                <CountdownIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                  {nextElectionCountdown.days}d {nextElectionCountdown.hours}h
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>
                  to {nextElectionCountdown.label.replace(/ Election.*/, '').substring(0, 14)}
                </Typography>
              </Box>
            )}

            {/* Global Refresh */}
            <IconButton
              size={isMobile ? "small" : "large"}
              onClick={handleRefresh}
              title="Refresh page data"
              sx={{
                transition: 'transform 0.6s ease',
                transform: isRefreshing ? 'rotate(360deg)' : 'none',
              }}
            >
              <RefreshIcon />
            </IconButton>

            {/* Actions Dropdown Menu */}
            <IconButton
              ref={actionsButtonRef}
              size={isMobile ? "small" : "large"}
              onClick={handleActionsMenuClick}
              title="Actions"
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Dropdown */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={Boolean(notificationsAnchorEl)}
        onClose={handleNotificationsClose}
        PaperProps={{
          sx: {
            mt: 2,
            width: 360,
            maxHeight: 480,
            overflow: 'auto',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Latest Updates
          </Typography>
        </Box>

        {notifications.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No new notifications</Typography>
          </Box>
        )}

        {notifications.map((notification) => (
          <MenuItem
            key={notification.id}
            onClick={handleNotificationsClose}
            sx={{
              py: 2,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
              backgroundColor: notification.read ? 'transparent' : 'action.hover',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {notification.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notification.time}
                  </Typography>
                </Box>
                {!notification.read && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      mt: 1,
                    }}
                  />
                )}
              </Box>
            </Box>
          </MenuItem>
        ))}

      </Menu>

      {/* Trending Candidates Dropdown */}
      <Menu
        anchorEl={trendingAnchorEl}
        open={Boolean(trendingAnchorEl)}
        onClose={handleTrendingClose}
        PaperProps={{
          sx: {
            mt: 2,
            width: 320,
            maxHeight: 480,
            overflow: 'auto',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('trending.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Real-time candidate performance
          </Typography>
        </Box>

        {trendingCandidates.map((candidate) => (
          <MenuItem
            key={candidate.id}
            onClick={() => {
              navigate(`/candidate/${candidate.id}`);
              handleTrendingClose();
            }}
            sx={{
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={candidate.profileImage}
                  sx={{
                    bgcolor: candidate.color,
                    width: 40,
                    height: 40,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {candidate.avatar}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {candidate.name}
                    </Typography>
                    {candidate.trend === 'up' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                        <TrendingIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {candidate.change}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                        <TrendingIcon sx={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                          {candidate.change}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {candidate.party}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {candidate.mentions}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </MenuItem>
        ))}

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              navigate('/candidates');
              handleTrendingClose();
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('trending.viewAll')}
          </Button>
        </Box>
      </Menu>

      {/* Actions Dropdown Menu */}
      <Menu
        anchorEl={actionsMenuAnchorEl}
        open={Boolean(actionsMenuAnchorEl)}
        onClose={handleActionsMenuClose}
        PaperProps={{
          sx: {
            mt: 2,
            minWidth: 320,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          handleActionsMenuClose();
          setTimeout(() => {
            if (actionsButtonRef.current) {
              handleNotificationsClick({ currentTarget: actionsButtonRef.current } as unknown as React.MouseEvent<HTMLElement>);
            }
          }, 100);
        }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon fontSize="small" />
            </Badge>
          </ListItemIcon>
          <ListItemText>{t('notifications.title')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          handleActionsMenuClose();
          setTimeout(() => {
            if (actionsButtonRef.current) {
              handleTrendingClick({ currentTarget: actionsButtonRef.current } as unknown as React.MouseEvent<HTMLElement>);
            }
          }, 100);
        }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Badge badgeContent={trendingCandidates.length} color="primary">
              <TrendingIcon fontSize="small" />
            </Badge>
          </ListItemIcon>
          <ListItemText>{t('trending.title')}</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => {
          const next = i18n.language === 'en' ? 'es' : 'en';
          i18n.changeLanguage(next);
          handleActionsMenuClose();
        }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <LanguageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('header.language')}: {i18n.language === 'en' ? 'English → Español' : 'Español → English'}
          </ListItemText>
        </MenuItem>
        
        {location.pathname.startsWith('/candidate/') && [
          <Divider key="divider" />,
          <MenuItem key="share" onClick={() => { 
            if (navigator.share) {
              navigator.share({
                title: `Candidate Profile`,
                text: `Check out this candidate profile`,
                url: window.location.href,
              });
            }
            handleActionsMenuClose(); 
          }}>
            <ListItemIcon>
              <ShareIcon />
            </ListItemIcon>
            <ListItemText>Share Profile</ListItemText>
          </MenuItem>,
          
          <MenuItem key="bookmark" onClick={() => { console.log('Bookmark candidate'); handleActionsMenuClose(); }}>
            <ListItemIcon>
              <BookmarkIcon />
            </ListItemIcon>
            <ListItemText>Bookmark</ListItemText>
          </MenuItem>
        ]}
      </Menu>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'primary.main',
              borderRight: 'none',
              display: { xs: 'none', md: 'block' },
              transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Mobile Navigation Menu */}
      {isMobile && (
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          PaperProps={{
            sx: { mt: 1, minWidth: 200 }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.text}
              selected={location.pathname === item.path}
              onClick={() => {
                if (item.path) {
                  handleNavigation(item.path);
                }
                handleMobileMenuClose();
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          ml: { xs: 0, md: `${drawerWidth}px` },
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

const Layout: React.FC = () => {
  return (
    <ViewModeProvider>
      <LayoutContent />
    </ViewModeProvider>
  );
};

export default Layout;
